import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import type {
  GameDetail,
  GamesSearchOrderBy,
  GamesSearchPage,
} from '../../lib/api/games-api-client';
import type { AffiliateLink } from '../../lib/api/affiliate-signed-url-client';

/**
 * Games slice state + actions (task 6.5, public-website spec).
 *
 * Per design §3.1 (Slices layer) the games slice carries two
 * keyed caches:
 *
 *  - `searchPagesByKey` — one entry per `(q, page, orderBy)` triple
 *    issued by `<GamesPage>` (task 6.8). The key format is
 *    `${q}|${page ?? 1}|${orderBy ?? 'release-date-desc'}` so the
 *    selectors in 6.6 can address pages without re-deriving the key.
 *    The empty-`q` default-landing experience (R20.7) reuses the
 *    same cache slot; the slice does not special-case it.
 *  - `gameDetailById` — one entry per `:gameId` route param. Each
 *    entry tracks both the detail-fetch lifecycle and a parallel
 *    affiliate-hydration lifecycle. The hydration step is the
 *    Game-page on-demand affiliate signed-URL transport per design
 *    §8.2: when the `GetGameByIdV1` response carries an empty
 *    `affiliateLinks` array, the affiliate thunk in
 *    `store/thunks/affiliate-thunks.ts` mints + fetches the signed
 *    URL and dispatches `gameDetailAffiliateLinksHydrated` to merge
 *    the result into the cached detail.
 *
 * The slice exposes a single shared `currentSearchKey` so
 * `<GamesPage>` can resolve "the most recent search the user
 * issued" without re-computing the key on render.
 *
 * Reducers stay pure — Redux Toolkit's `createSlice` wraps every
 * mutation in Immer so the assignments below are safe inside the
 * `produce` proxy and produce a new immutable state object.
 *
 * Function-length budget: every reducer below stays under 20 lines
 * per Requirement 3.7.
 */

/** Per-key fetch status discriminator. Mirrors the content slice. */
export type GamesFetchStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

/**
 * Per-search-key cache entry. `data` is populated on success;
 * `error` is populated on failure. Both are `undefined` while a
 * request is in flight.
 */
export interface GamesSearchPageEntry {
  readonly status: GamesFetchStatus;
  readonly data?: GamesSearchPage;
  readonly error?: string;
}

/**
 * Per-game-id cache entry. Carries the detail-fetch lifecycle plus
 * a parallel affiliate-hydration lifecycle so the Game-page can
 * render the body while the affiliate buttons are still loading.
 */
export interface GameDetailEntry {
  readonly status: GamesFetchStatus;
  readonly data?: GameDetail;
  readonly error?: string;
  readonly affiliateHydrationStatus?: GamesFetchStatus;
  readonly affiliateError?: string;
}

/** Games slice state shape per design §3.1. */
export interface GamesState {
  /** Per-`(q, page, orderBy)` cache of search-page payloads. */
  readonly searchPagesByKey: Record<string, GamesSearchPageEntry>;
  /** Per-id cache of detail payloads + affiliate-hydration lifecycle. */
  readonly gameDetailById: Record<string, GameDetailEntry>;
  /** Last-issued search key for selectors (see task 6.6). */
  readonly currentSearchKey?: string;
}

/** Stable input shape consumed by `gamesSearchRequested`. */
export interface GamesSearchInput {
  readonly q: string;
  readonly page?: number;
  readonly orderBy?: GamesSearchOrderBy;
}

/**
 * Compute the canonical search key for a `(q, page, orderBy)`
 * triple per design §3.1 (Slices layer). Exported so the
 * thunks in 6.5 and the selectors in 6.6 produce identical keys
 * without duplicating the format string.
 *
 * Defaults: `page ?? 1`, `orderBy ?? 'release-date-desc'` per
 * Requirement 20.7.
 *
 * @param input The search input.
 * @returns The canonical key string `${q}|${page}|${orderBy}`.
 */
export function buildGamesSearchKey(input: GamesSearchInput): string {
  const page = input.page ?? 1;
  const orderBy = input.orderBy ?? 'release-date-desc';
  return `${input.q}|${page}|${orderBy}`;
}

const initialState: GamesState = {
  searchPagesByKey: {},
  gameDetailById: {},
  currentSearchKey: undefined,
};

export const gamesSlice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    /**
     * Mark a `(q, page, orderBy)` triple as in-flight and record
     * it as the most-recent search.
     */
    gamesSearchRequested(state, action: PayloadAction<GamesSearchInput>) {
      const key = buildGamesSearchKey(action.payload);
      state.searchPagesByKey[key] = { status: 'loading' };
      state.currentSearchKey = key;
    },
    /** Cache the page payload for a search key. */
    gamesSearchSucceeded(
      state,
      action: PayloadAction<{ key: string; page: GamesSearchPage }>,
    ) {
      state.searchPagesByKey[action.payload.key] = {
        status: 'succeeded',
        data: castDraft(action.payload.page),
      };
    },
    /** Record the failure message for a search key. */
    gamesSearchFailed(
      state,
      action: PayloadAction<{ key: string; error: string }>,
    ) {
      state.searchPagesByKey[action.payload.key] = {
        status: 'failed',
        error: action.payload.error,
      };
    },
    /** Drop every cached search page and clear the current key. */
    gamesSearchCleared(state) {
      state.searchPagesByKey = {};
      state.currentSearchKey = undefined;
    },
    /** Mark a detail fetch for a game id as in-flight. */
    gameDetailRequested(state, action: PayloadAction<string>) {
      state.gameDetailById[action.payload] = { status: 'loading' };
    },
    /** Cache the detail payload for a game id. */
    gameDetailSucceeded(
      state,
      action: PayloadAction<{ id: string; detail: GameDetail }>,
    ) {
      const existing = state.gameDetailById[action.payload.id];
      state.gameDetailById[action.payload.id] = {
        status: 'succeeded',
        data: castDraft(action.payload.detail),
        affiliateHydrationStatus: existing?.affiliateHydrationStatus,
        affiliateError: existing?.affiliateError,
      };
    },
    /** Record the failure message for a game-id detail fetch. */
    gameDetailFailed(
      state,
      action: PayloadAction<{ id: string; error: string }>,
    ) {
      state.gameDetailById[action.payload.id] = {
        status: 'failed',
        error: action.payload.error,
      };
    },
    /** Mark affiliate hydration for a game id as in-flight. */
    affiliateHydrationRequested(state, action: PayloadAction<string>) {
      const entry = state.gameDetailById[action.payload] ?? { status: 'idle' };
      state.gameDetailById[action.payload] = {
        ...entry,
        affiliateHydrationStatus: 'loading',
        affiliateError: undefined,
      };
    },
    /** Record the failure message for affiliate hydration. */
    affiliateHydrationFailed(
      state,
      action: PayloadAction<{ id: string; error: string }>,
    ) {
      const entry = state.gameDetailById[action.payload.id] ?? { status: 'idle' };
      state.gameDetailById[action.payload.id] = {
        ...entry,
        affiliateHydrationStatus: 'failed',
        affiliateError: action.payload.error,
      };
    },
    /**
     * Merge signed-URL-fetched affiliate links into the existing
     * `GameDetail` record per design §8.2. Replaces the cached
     * `affiliateLinks` array when `links` is non-empty; an empty
     * `links` array leaves any prior list intact and just records
     * `affiliateHydrationStatus = 'succeeded'`.
     */
    gameDetailAffiliateLinksHydrated(
      state,
      action: PayloadAction<{ id: string; links: ReadonlyArray<AffiliateLink> }>,
    ) {
      const entry = state.gameDetailById[action.payload.id] ?? { status: 'idle' };
      const data = entry.data;
      const merged: GameDetail | undefined =
        data && action.payload.links.length > 0
          ? { ...data, affiliateLinks: action.payload.links }
          : data;
      state.gameDetailById[action.payload.id] = {
        ...entry,
        data: castDraft(merged),
        affiliateHydrationStatus: 'succeeded',
        affiliateError: undefined,
      };
    },
  },
});

export const {
  gamesSearchRequested,
  gamesSearchSucceeded,
  gamesSearchFailed,
  gamesSearchCleared,
  gameDetailRequested,
  gameDetailSucceeded,
  gameDetailFailed,
  affiliateHydrationRequested,
  affiliateHydrationFailed,
  gameDetailAffiliateLinksHydrated,
} = gamesSlice.actions;

export default gamesSlice.reducer;
